import { useEffect } from 'react'

import { RichTextEditor } from '@mantine/tiptap'
import { useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { htmlToMarkdown, markdownToHtml } from '../lib/knowledgeBaseMarkdown'

interface KnowledgeBaseEditorProps {
  /** Knowledge base content in markdown (as persisted / sent to Bland AI). */
  value: string
  onChange?: (markdown: string) => void
  editable?: boolean
}

/**
 * Rich-text editor for the voice agent knowledge base. Clients edit with
 * familiar formatting controls (bold, italic, bullets) while the content is
 * stored as markdown — the text is ingested verbatim by Bland AI, so HTML
 * must never be persisted. Conversion happens at this component's boundary
 * via knowledgeBaseMarkdown utils.
 */
const KnowledgeBaseEditor = ({
  value,
  onChange,
  editable = true,
}: KnowledgeBaseEditorProps) => {
  const editor = useEditor({
    editable,
    extensions: [
      StarterKit.configure({
        heading: { levels: [1, 2, 3] },
        orderedList: false,
        strike: false,
        code: false,
        codeBlock: false,
        blockquote: false,
      }),
    ],
    content: markdownToHtml(value),
    onUpdate: ({ editor }) => {
      onChange?.(editor.isEmpty ? '' : htmlToMarkdown(editor.getHTML()))
    },
  })

  useEffect(() => {
    editor?.setEditable(editable)
  }, [editable, editor])

  useEffect(() => {
    if (editor && value !== htmlToMarkdown(editor.getHTML())) {
      editor.commands.setContent(markdownToHtml(value), false)
    }
  }, [value, editor])

  return (
    <RichTextEditor editor={editor} variant="subtle">
      {editable && (
        <RichTextEditor.Toolbar>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.H1 />
            <RichTextEditor.H2 />
            <RichTextEditor.H3 />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.Bold />
            <RichTextEditor.Italic />
          </RichTextEditor.ControlsGroup>
          <RichTextEditor.ControlsGroup>
            <RichTextEditor.BulletList />
            <RichTextEditor.Hr />
          </RichTextEditor.ControlsGroup>
        </RichTextEditor.Toolbar>
      )}
      <RichTextEditor.Content className="min-h-40 text-sm" />
    </RichTextEditor>
  )
}

export default KnowledgeBaseEditor
